import { useState, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { scrollToSection } from "@/components/Navbar";

const COURSES = [
  {
    id: "german",
    name: "German",
    native: "Deutsch",
    levels: "A1 – C1 · Goethe Prep",
    desc: "Your gateway to study & work in Germany, Austria and Switzerland.",
    img: "https://images.unsplash.com/photo-1554072675-66db59dba46f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxiZXJsaW4lMjBzdHJlZXR8ZW58MHx8fHwxNzg4MTEwMjgxfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "french",
    name: "French",
    native: "Français",
    levels: "A1 – B2 · DELF Prep",
    desc: "The language of diplomacy, cuisine and 29 countries worldwide.",
    img: "https://images.unsplash.com/photo-1609971757431-439cf7b4141b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxwYXJpcyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODgxMTAyODB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "korean",
    name: "Korean",
    native: "한국어",
    levels: "TOPIK I & II Prep",
    desc: "Ride the Hallyu wave — careers, scholarships and culture in Korea.",
    img: "https://images.unsplash.com/photo-1588676907230-020a495f4cc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxzZW91bCUyMHRyYWRpdGlvbmFsfGVufDB8fHx8MTc4ODExMDI4MXww&ixlib=rb-4.1.0&q=85",
  },
];

export default function Courses() {
  const [active, setActive] = useState(null);
  const containerRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 160, damping: 22 });
  const sy = useSpring(my, { stiffness: 160, damping: 22 });

  const onMove = (e) => {
    mx.set(e.clientX);
    my.set(e.clientY);
  };

  return (
    <section id="courses" ref={containerRef} onMouseMove={onMove} className="relative py-28 lg:py-40 overflow-hidden" data-testid="courses-section">
      <div className="absolute left-[-15%] top-[-10%] w-[45vw] h-[45vw] max-w-[700px] max-h-[700px] rounded-full bg-[#1D5FD6]/12 blur-[130px] pointer-events-none" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C44900] mb-6" data-testid="courses-eyebrow">01 — The Courses</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tighter text-[#F4F4F0] max-w-3xl leading-[1.02]">
            Three languages.<br /><em className="italic text-[#A3A3A3]">Infinite directions.</em>
          </h2>
        </Reveal>

        <div className="mt-16 lg:mt-24 border-t border-white/10">
          {COURSES.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <button
                onClick={() => scrollToSection("#contact")}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                className="group w-full text-left border-b border-white/10 py-8 lg:py-12 grid grid-cols-[auto_1fr_auto] items-center gap-5 lg:gap-10"
                data-testid={`course-row-${c.id}`}
              >
                <span className="font-display text-sm lg:text-lg text-[#A3A3A3] w-8">0{i + 1}</span>
                <span>
                  <span className="block font-display text-[clamp(2.2rem,6vw,5rem)] leading-none tracking-tighter text-[#F4F4F0] transition-colors duration-500 group-hover:text-[#C44900]">
                    {c.name}
                    <span className="ml-4 align-middle font-display italic text-base lg:text-2xl text-[#A3A3A3]">{c.native}</span>
                  </span>
                  <span className="mt-3 block text-xs uppercase tracking-[0.22em] text-[#A3A3A3]">{c.levels}</span>
                  <span className="mt-2 block md:hidden"><img src={c.img} alt={c.name} className="w-full h-44 object-cover rounded-md" loading="lazy" /></span>
                  <span className="mt-3 hidden md:block max-w-lg text-sm text-[#A3A3A3] leading-relaxed">{c.desc}</span>
                </span>
                <span className="hidden md:block w-24 h-16 shrink-0 overflow-hidden rounded-md border border-white/10">
                  <img src={c.img} alt="" loading="lazy" className="w-full h-full object-cover grayscale opacity-60 transition-all duration-500 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110" />
                </span>
                <span className="w-11 h-11 lg:w-14 lg:h-14 rounded-full border border-white/15 flex items-center justify-center text-[#A3A3A3] transition-all duration-500 group-hover:bg-[#C44900] group-hover:border-[#C44900] group-hover:text-[#F4F4F0] group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            key={active}
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.9, rotate: 3 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ left: sx, top: sy }}
            className="pointer-events-none fixed z-30 hidden md:block w-72 h-96 -translate-x-1/2 -translate-y-[110%] overflow-hidden rounded-lg border border-white/15"
            data-testid="course-hover-image"
          >
            <img src={COURSES[active].img} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <span className="absolute bottom-4 left-4 text-xs uppercase tracking-[0.25em] text-[#F4F4F0]">{COURSES[active].native}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
